import { useEffect, useMemo, useState } from "react";

import useMeetingStore from "@store/useMeetingStore";
import { useGetTags } from "@screens/thread/hooks/useGetTags";
import { useGetMeeting } from "@screens/meeting/hooks/useGetMeeting";
import { useGetDiscussedTopics } from "./hooks/useGetDiscussedTopics";
import { useOptimisticTags } from "@components/Sections/TagsSection/hooks/useOptimisticTags";
import { useTimeoutRef } from "@hooks/useTimeoutRef";
import { User } from "@app-types/common.type";
import { RelationObjectType } from "@enums/relations.enum";
import { IExternalAttendee } from "@interfaces/meeting.interface";
import { MeetingTagsSection } from "@components/Sections/TagsSection";
import { MeetingTopicsSection } from "@components/Sections/TagsSection";
import { AttendeesSection } from "@components/Sections/AttendeesSection";
import { NoteSection } from "@components/Sections/NoteSection";
import { NewEntityButtons } from "@components/AddNewEntity";
import { MeetingHeader } from "@components/Headers";
import { View } from "@components/ui/View/View";

const CONTENT_TYPE = RelationObjectType.MEETING;

export default function MeetingScreen() {
  const { meetingId, isFirstLoading, setIsFirstLoading } = useMeetingStore();
  const { data: meetingData } = useGetMeeting(!isFirstLoading);
  const { data: tags, isFetching: isFetchingTags } = useGetTags(CONTENT_TYPE, meetingId);
  const { data: topics } = useGetDiscussedTopics(CONTENT_TYPE, meetingId);
  const meeting = meetingData?.meeting;
  const isSyncedRecurringSeries = meetingData?.is_synced && meetingData?.is_recurring;

  // Single tag-state owner, shared by the Tags section and the "New Company/Deal" buttons.
  const { currentTags, updateTags, onRemoveTag, setCurrentTags, temporaryTags } = useOptimisticTags(
    CONTENT_TYPE,
    meetingId,
    tags || []
  );

  useEffect(() => {
    if (tags) setCurrentTags(tags);
  }, [tags, setCurrentTags]);

  const attendees = useMemo(() => {
    // Transform attendees to ensure all of them have valid IDs
    return (
      meeting?.attendees?.map((attendee: User | IExternalAttendee) => {
        if ("id" in attendee) {
          return attendee;
        }

        return { ...attendee, id: attendee?.email };
      }) || []
    );
  }, [meeting?.attendees]);

  const [showTopics, setShowTopics] = useState(!isFirstLoading);
  const [showTeam, setShowTeam] = useState(!isFirstLoading);
  const [showTags, setShowTags] = useState(!isFirstLoading);
  const { schedule: scheduleShowTeam } = useTimeoutRef();
  const { schedule: scheduleShowTags } = useTimeoutRef();

  useEffect(() => {
    if (typeof topics === "undefined") return;

    setShowTopics(true);
    scheduleShowTeam(() => setShowTeam(true), 100);
    scheduleShowTags(() => {
      setShowTags(true);
      setIsFirstLoading(false);
    }, 200);
  }, [topics, scheduleShowTeam, scheduleShowTags]);

  useEffect(() => {
    if (typeof tags === "undefined") return;

    setShowTags(true);
    setIsFirstLoading(false);
  }, [tags]);

  if (isSyncedRecurringSeries) {
    return (
      <div className="flex flex-col justify-center items-center px-4 py-[8px_30px]">
        <MeetingHeader isRecurringSeries />

        <p className="mt-6 text-gray-450/95 dark:text-gray-100/90">
          All meetings in this series are synced to Meridian.
          To edit details or tags, open an individual meeting occurrence in Outlook.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center px-4 py-[8px_30px]">
      <MeetingHeader
        meeting={meeting}
        isNewlySynced={meetingData?.newly_synced}
        hasTagsOrTopics={tags?.length > 0 || topics?.length > 0}
      />

      <div className="relative z-0 flex flex-col gap-6 py-6 w-full animate-fade-in">
        <NoteSection objectId={meetingId} contentType={CONTENT_TYPE} />

        <NewEntityButtons contentType={CONTENT_TYPE} onEntityCreated={(option) => updateTags([option])} />

        <View.Condition if={showTopics}>
          <MeetingTopicsSection
            topics={topics}
            contentType={CONTENT_TYPE}
            className="animate-fade-in"
          />
        </View.Condition>

        <View.Condition if={showTeam}>
          <AttendeesSection
            attendees={attendees as User[]}
            people={meeting?.people || []}
            className="animate-fade-in"
            size="medium"
          />
        </View.Condition>

        <View.Condition if={showTags}>
          <MeetingTagsSection
            contentType={CONTENT_TYPE}
            className="animate-fade-in"
            isUpdating={isFetchingTags}
            currentTags={currentTags}
            updateTags={updateTags}
            onRemoveTag={onRemoveTag}
            temporaryTags={temporaryTags}
          />
        </View.Condition>
      </div>
    </div>
  );
}
